import { useCallback, useEffect, useMemo, useState } from "react";

const RANGES = [
  { key: "7d", label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
  { key: "90d", label: "Last 90 days" },
];

// Tiny country-code → emoji flag map. Falls back to "🌐" for codes we
// don't recognize, which is fine — the visitor count tells the story.
function flagEmoji(cc) {
  if (!cc || cc.length !== 2) return "🌐";
  const A = 0x1f1e6;
  const code = cc.toUpperCase();
  return String.fromCodePoint(A + code.charCodeAt(0) - 65) +
    String.fromCodePoint(A + code.charCodeAt(1) - 65);
}

const ENGINE_LABEL = {
  landing: "Landing",
  matrix: "Restoration Matrix",
  warfare: "Spiritual Warfare",
  progress: "Progress Engine",
  chat: "Chat with the OS",
  library: "Library",
  community: "Community",
  donate: "Donate",
  admin: "Admin",
  other: "Other",
};

function formatNumber(n) {
  if (n === null || n === undefined) return "—";
  return Number(n).toLocaleString();
}

// Render a 7/30/90-day sparkline. Pure SVG, no chart library — keeps the
// bundle thin and the visual style consistent with the rest of the app.
function Sparkline({ data, height = 70 }) {
  const points = data || [];
  if (points.length < 2) {
    return (
      <div className="text-sm text-[#F0E6D2]/50 italic">
        Not enough data yet.
      </div>
    );
  }
  const W = 720;
  const H = height;
  const max = Math.max(...points.map((p) => p.visits), 1);
  const step = points.length > 1 ? W / (points.length - 1) : W;
  const linePath = points
    .map((p, i) => {
      const x = i * step;
      const y = H - 6 - (p.visits / max) * (H - 12);
      return `${i === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");
  const areaPath = `${linePath} L${W},${H} L0,${H} Z`;
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      className="w-full h-[70px] block"
      data-testid="analytics-timeseries"
    >
      <defs>
        <linearGradient id="rm-spark-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FFE7A0" stopOpacity="0.45" />
          <stop offset="100%" stopColor="#FFE7A0" stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill="url(#rm-spark-fill)" />
      <path d={linePath} fill="none" stroke="#FFE7A0" strokeWidth="1.6" />
    </svg>
  );
}

// Horizontal bar with a label, count, and percent-of-max width. We use
// max-of-set (not total) so even small countries are still visible when
// the top one dominates.
function BarRow({ label, accent, visits, uniques, max, onClick, testid }) {
  const pct = max > 0 ? Math.max(2, (visits / max) * 100) : 0;
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={`rm-analytics-bar group ${onClick ? "cursor-pointer" : ""}`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between gap-3 mb-1">
        <span className="text-[#F0E6D2]/90 text-sm flex items-center gap-2 min-w-0 truncate">
          {accent && <span className="shrink-0 text-base leading-none">{accent}</span>}
          <span className="truncate">{label}</span>
        </span>
        <span className="text-[#FFE7A0]/85 text-xs font-mono tracking-wide shrink-0">
          {formatNumber(visits)} visits
          {uniques !== undefined && uniques !== null ? (
            <span className="text-[#F0E6D2]/55"> · {formatNumber(uniques)} unique</span>
          ) : null}
        </span>
      </div>
      <div className="rm-analytics-bar-track">
        <div
          className="rm-analytics-bar-fill"
          style={{ width: `${pct}%` }}
        />
      </div>
    </Tag>
  );
}

function Card({ title, subtitle, children, action }) {
  return (
    <section
      className="rm-analytics-card"
      data-testid={`analytics-card-${title.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <header className="flex items-baseline justify-between gap-3 mb-3">
        <div>
          <h3 className="font-serif-display italic text-2xl text-[#FFE7A0]">
            {title}
          </h3>
          {subtitle && (
            <p className="text-xs text-[#F0E6D2]/55 mt-0.5">{subtitle}</p>
          )}
        </div>
        {action}
      </header>
      {children}
    </section>
  );
}

export default function AdminAnalyticsPanel({ adminAxios }) {
  const [range, setRange] = useState("30d");
  const [country, setCountry] = useState(null);   // {country_code, country}
  const [state, setState] = useState(null);       // string
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [packetStats, setPacketStats] = useState(null);

  const load = useCallback(async () => {
    if (!adminAxios) return;
    setLoading(true);
    setError("");
    try {
      const params = { range };
      if (country?.country_code) params.country = country.country_code;
      if (state) params.state = state;
      const res = await adminAxios.get("/admin/analytics/summary", { params });
      setData(res.data || null);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not load analytics.");
    } finally {
      setLoading(false);
    }
  }, [adminAxios, range, country, state]);

  const loadPacketStats = useCallback(async () => {
    if (!adminAxios) return;
    try {
      const res = await adminAxios.get("/admin/packet-download-stats");
      setPacketStats(res.data || null);
    } catch {
      // Non-fatal — the rest of the analytics panel still works.
      setPacketStats(null);
    }
  }, [adminAxios]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadPacketStats(); }, [loadPacketStats]);

  const geoMax = useMemo(() => {
    if (!data?.geography?.length) return 0;
    return Math.max(...data.geography.map((g) => g.visits || 0));
  }, [data]);
  const pageMax = useMemo(() => {
    if (!data?.pages?.length) return 0;
    return Math.max(...data.pages.map((p) => p.visits || 0));
  }, [data]);

  const breadcrumb = useMemo(() => {
    const parts = ["All countries"];
    if (country) parts.push(country.country || country.country_code);
    if (state) parts.push(state);
    return parts;
  }, [country, state]);

  return (
    <div className="rm-analytics-wrap" data-testid="analytics-panel">
      {/* Range selector */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <p className="text-sm text-[#F0E6D2]/65 leading-snug max-w-2xl">
          Privacy-preserving page-view tracking. No IPs stored. Visitors are
          identified by an anonymous cookie ID only.
        </p>
        <div className="rm-analytics-range">
          {RANGES.map((r) => (
            <button
              key={r.key}
              type="button"
              onClick={() => setRange(r.key)}
              className={`rm-analytics-range-btn${range === r.key ? " is-active" : ""}`}
              data-testid={`analytics-range-${r.key}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="rm-cm-alert rm-cm-alert-error mb-4" data-testid="analytics-error">
          {error}
        </div>
      )}

      {/* Three cards stacked: Visitors, Geography, Pages */}
      <div className="space-y-6">
        {/* Visitors */}
        <Card
          title="Visitors"
          subtitle={loading ? "Loading…" : `Range: ${range}`}
        >
          <div className="grid grid-cols-2 gap-4 sm:gap-6 mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#F0E6D2]/45">
                Unique
              </div>
              <div
                className="font-serif-display text-4xl sm:text-5xl text-[#FFE7A0] leading-none"
                data-testid="analytics-uniques"
              >
                {loading ? "…" : formatNumber(data?.unique_visitors ?? 0)}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#F0E6D2]/45">
                Total visits
              </div>
              <div
                className="font-serif-display text-4xl sm:text-5xl text-[#FFE7A0] leading-none"
                data-testid="analytics-visits"
              >
                {loading ? "…" : formatNumber(data?.total_visits ?? 0)}
              </div>
            </div>
          </div>
          <Sparkline data={data?.timeseries || []} />
        </Card>

        {/* Geography */}
        <Card
          title="Geography"
          subtitle={breadcrumb.join("  ›  ")}
          action={
            (country || state) ? (
              <button
                type="button"
                onClick={() => { setCountry(null); setState(null); }}
                className="rm-cm-link text-xs"
                data-testid="analytics-geo-reset"
              >
                ← Back to all countries
              </button>
            ) : null
          }
        >
          {!loading && (!data?.geography || data.geography.length === 0) ? (
            <div className="rm-cm-empty" data-testid="analytics-geo-empty">
              No visitors recorded yet for this range.
            </div>
          ) : (
            <ul className="space-y-1.5" data-testid="analytics-geo-list">
              {(data?.geography || []).map((g, idx) => {
                const isCountryLevel = !state && !country;
                const isStateLevel = !state && !!country;
                let label = g.city || g.state || g.country || "Unknown";
                let accent = "•";
                if (isCountryLevel) {
                  label = g.country || "Unknown";
                  accent = flagEmoji(g.country_code);
                } else if (isStateLevel) {
                  label = g.state || "Unknown";
                  accent = "📍";
                } else {
                  label = g.city || "Unknown";
                  accent = "🏙";
                }
                const onClick = isCountryLevel && g.country_code
                  ? () => { setCountry({ country_code: g.country_code, country: g.country }); setState(null); }
                  : isStateLevel && g.state
                    ? () => setState(g.state)
                    : null;
                return (
                  <BarRow
                    key={`${label}-${idx}`}
                    label={label}
                    accent={accent}
                    visits={g.visits}
                    uniques={g.uniques}
                    max={geoMax}
                    onClick={onClick}
                    testid={`analytics-geo-row-${idx}`}
                  />
                );
              })}
            </ul>
          )}
        </Card>

        {/* Pages / Engines */}
        <Card
          title="What they're using"
          subtitle="Visits per area of the site"
        >
          {!loading && (!data?.pages || data.pages.length === 0) ? (
            <div className="rm-cm-empty" data-testid="analytics-pages-empty">
              No page activity yet.
            </div>
          ) : (
            <ul className="space-y-1.5" data-testid="analytics-pages-list">
              {(data?.pages || []).map((p, idx) => (
                <BarRow
                  key={`${p.engine}-${idx}`}
                  label={ENGINE_LABEL[p.engine] || p.engine}
                  visits={p.visits}
                  uniques={p.uniques}
                  max={pageMax}
                  testid={`analytics-pages-row-${idx}`}
                />
              ))}
            </ul>
          )}
        </Card>

        {/* Packet downloads — global counter (packets themselves are
            ephemeral and PII-scrubbed; this is the only persistent
            record that a packet was ever issued). */}
        <Card
          title="Packet downloads"
          subtitle="Lifetime total · last 100 events"
        >
          <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-4 sm:gap-8 items-start">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#F0E6D2]/45">
                Total downloads
              </div>
              <div
                className="font-serif-display text-4xl sm:text-5xl text-[#FFE7A0] leading-none"
                data-testid="analytics-packet-total"
              >
                {packetStats === null
                  ? "…"
                  : formatNumber(packetStats.total ?? 0)}
              </div>
              <p className="text-xs text-[#F0E6D2]/55 mt-2 max-w-[16rem]">
                Each download = one recipient pulled their PDF.
                Names are first-name only; packets themselves are
                deleted after 1 hour.
              </p>
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#F0E6D2]/45 mb-2">
                Recent
              </div>
              {!packetStats || (packetStats.recent || []).length === 0 ? (
                <div className="rm-cm-empty" data-testid="analytics-packet-empty">
                  No packet downloads recorded yet.
                </div>
              ) : (
                <ul
                  className="rm-analytics-packet-recent"
                  data-testid="analytics-packet-recent"
                >
                  {(packetStats.recent || []).slice(0, 25).map((ev, idx) => (
                    <li key={`${ev.ts}-${idx}`} className="flex justify-between gap-3 py-1 border-b border-[#F0E6D2]/10 text-sm">
                      <span className="text-[#F0E6D2]/85 truncate">
                        {ev.first_name || "Anonymous"}
                      </span>
                      <span className="text-[#F0E6D2]/45 text-xs whitespace-nowrap">
                        {ev.ts ? new Date(ev.ts).toLocaleString() : ""}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
